import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import { axiosApi, getResponseError, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_QAS, URL_QAS } from "@/constants/api_endpoints";
import {
	CategoryFrom,
	ICategory,
	IEditCategoryProps,
	IQasCategoryState,
	IResponseAddingCategory,
	IResponseCategoriesList,
	IResponseImportCategory,
} from "@/types/qas/qasCategoryTypes";
import { IResponse, RequestStatus, ResponseStatus } from "@/types/statusTypes";

const initialState: IQasCategoryState = {
	categoriesList: {
		status: RequestStatus.IDLE,
		data: [],
		dictionary: {},
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	addingCategory: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		id: null,
		from: null,
	},
	editingCategory: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	deletingCategory: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// получение списка категорий
export const getCategoriesList = createAsyncThunk("qasCategory/list", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseCategoriesList | IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.CATEGORY_LIST}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// добавление категории
export const addCategory = createAsyncThunk("qasCategory/add", async (categoryName: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseAddingCategory> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.CATEGORY_ADD}`, {
			name: categoryName,
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// изменение категории
export const editCategory = createAsyncThunk("qasCategory/edit", async ({ categoryId, categoryName }: IEditCategoryProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.CATEGORY_EDIT}/${categoryId}`, {
			name: categoryName,
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// удаление категории
export const deleteCategory = createAsyncThunk("qasCategory/delete", async (categoryId: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.delete(`${URL_QAS}/${ENDPOINTS_QAS.CATEGORY_DELETE}/${categoryId}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// импорт категории
export const importCategory = createAsyncThunk(
	"qasCategory/import",
	async ({ file, id, datasetId }: { file: File; datasetId: string; id: string }, { rejectWithValue }) => {
		try {
			const formData = new FormData();
			formData.append("file", file);

			const response: AxiosResponse<IResponseImportCategory> = await axiosApi.post(
				`${URL_QAS}/${ENDPOINTS_QAS.CATEGORY_IMPORT}/${id}`,
				{ zip: formData.get("file") },
				{
					params: { dataset: datasetId },
					headers: {
						"Content-Type": "multipart/form-data",
					},
				}
			);
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// экспорт категории
export const exportCategory = createAsyncThunk("qasCategory/export", async ({ id, datasetId }: { id: string; datasetId: string }, { rejectWithValue }) => {
	try {
		const response = await axiosApi.get<Blob>(`${URL_QAS}/${ENDPOINTS_QAS.CATEGORY_EXPORT}/${id}`, {
			params: { dataset: datasetId },
			responseType: "blob",
		});
		return response.data; // Blob
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

const qasCategorySlice = createSlice({
	name: "qasCategory",
	initialState,
	reducers: {
		// добавление новой категории в список
		addCategoryInList: (state, action: PayloadAction<ICategory>) => {
			state.categoriesList.data.push(action.payload);
		},
		// удаление категории из списка
		deleteCategoryFromList: (state, action: PayloadAction<string>) => {
			const index = state.categoriesList.data.findIndex((categoryItem) => categoryItem.name === action.payload);
			state.categoriesList.data.splice(index, 1);
		},
		// изменение места добавления категории
		changePlaceAddingCategory: (state, action: PayloadAction<CategoryFrom>) => {
			state.addingCategory.from = action.payload;
		},
		// очистка state
		clearState: (state) => {
			state.categoriesList = initialState.categoriesList;
			state.addingCategory = initialState.addingCategory;
			state.editingCategory = initialState.editingCategory;
			state.deletingCategory = initialState.deletingCategory;
		},
		// очистка списка категорий
		clearCategoriesList: (state) => {
			state.categoriesList = initialState.categoriesList;
		},
		// очистка статуса добавления категории
		clearAddingCategory: (state) => {
			state.addingCategory = initialState.addingCategory;
		},
		// очистка статуса изменения категории
		clearEditingCategory: (state) => {
			state.editingCategory = initialState.editingCategory;
		},
		// очистка статуса удаления категории
		clearDeletingCategory: (state) => {
			state.deletingCategory = initialState.deletingCategory;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getCategoriesList.pending, (state) => {
				state.categoriesList.status = RequestStatus.LOADING;
			})
			.addCase(getCategoriesList.fulfilled, (state, action) => {
				if (typeof action.payload === "object" && "categories" in action.payload) {
					state.categoriesList.status = RequestStatus.IDLE;
					state.categoriesList.data = action.payload.categories.sort((a, b) => {
						if (a.name > b.name) return 1;
						else if (a.name < b.name) return -1;
						else return 0;
					});
					const dictionary: Record<string, string> = {}; // создаем словарь id-name
					action.payload.categories.forEach((categoryElem) => (dictionary[categoryElem.id] = categoryElem.name));
					state.categoriesList.dictionary = dictionary;
				} else state.categoriesList.status = RequestStatus.FAILED;
			})
			.addCase(getCategoriesList.rejected, (state, action: PayloadAction<unknown>) => {
				state.categoriesList.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.categoriesList.error = error;
				state.categoriesList.message = message;
			})
			.addCase(addCategory.pending, (state) => {
				state.addingCategory.status = RequestStatus.LOADING;
			})
			.addCase(addCategory.fulfilled, (state, action) => {
				if (action.payload) {
					state.addingCategory.status = RequestStatus.IDLE;
					state.addingCategory.error = action.payload.error;
					state.addingCategory.message = action.payload.message;
					if (action.payload.id) state.addingCategory.id = action.payload.id;
				} else state.addingCategory.status = RequestStatus.FAILED;
			})
			.addCase(addCategory.rejected, (state, action: PayloadAction<unknown>) => {
				state.addingCategory.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.addingCategory.error = error;
				state.addingCategory.message = message;
			})
			.addCase(editCategory.pending, (state) => {
				state.editingCategory.status = RequestStatus.LOADING;
			})
			.addCase(editCategory.fulfilled, (state, action) => {
				if (action.payload) {
					state.editingCategory.status = RequestStatus.IDLE;
					state.editingCategory.error = action.payload.error;
					state.editingCategory.message = action.payload.message;
				} else state.editingCategory.status = RequestStatus.FAILED;
			})
			.addCase(editCategory.rejected, (state, action: PayloadAction<unknown>) => {
				state.editingCategory.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.editingCategory.error = error;
				state.editingCategory.message = message;
			})
			.addCase(deleteCategory.pending, (state) => {
				state.deletingCategory.status = RequestStatus.LOADING;
			})
			.addCase(deleteCategory.fulfilled, (state, action) => {
				if (action.payload) {
					state.deletingCategory.status = RequestStatus.IDLE;
					state.deletingCategory.error = action.payload.error;
					state.deletingCategory.message = action.payload.message;
				} else state.deletingCategory.status = RequestStatus.FAILED;
			})
			.addCase(deleteCategory.rejected, (state, action: PayloadAction<unknown>) => {
				state.deletingCategory.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.deletingCategory.error = error;
				state.deletingCategory.message = message;
			});
	},
});

export const {
	addCategoryInList,
	deleteCategoryFromList,
	changePlaceAddingCategory,
	clearState,
	clearCategoriesList,
	clearAddingCategory,
	clearEditingCategory,
	clearDeletingCategory,
} = qasCategorySlice.actions;

export const selectCategoriesList = (state: RootState) => state.qasCategory.categoriesList;
export const selectAddingCategory = (state: RootState) => state.qasCategory.addingCategory;
export const selectEditingCategory = (state: RootState) => state.qasCategory.editingCategory;
export const selectDeletingCategory = (state: RootState) => state.qasCategory.deletingCategory;

export default qasCategorySlice.reducer;
